import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  CircularProgress,
  Alert
} from '@mui/material';
import { platformApiFetch } from './platformApi';

export default function PlatformAuditLogDetailPage() {
  const { logId } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/audit-logs/${logId}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Not found');
      setEntry(json.entry);
    } catch (e) {
      setError(e.message);
      setEntry(null);
    } finally {
      setLoading(false);
    }
  }, [logId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !entry) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!entry) return null;

  const row = (label, value) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
        {value ?? '—'}
      </Typography>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h4">Platform audit #{entry.id}</Typography>
          <Typography variant="body2" color="text.secondary">
            {entry.created_at ? String(entry.created_at) : ''}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate('/platform/audit-logs')}>
          Back to list
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {row('Scope', entry.scope)}
          {row('Actor user ID', entry.actor_user_id)}
          {row('Actor email', entry.actor_email)}
          {row('Actor type', entry.actor_type)}
          {row('Action', entry.action)}
          {row('Module', entry.module_name)}
          {row('Entity type', entry.entity_type)}
          {row('Entity ID', entry.entity_id)}
          {row('Summary', entry.summary)}
          {row('IP address', entry.ip_address)}
          {row('User agent', entry.user_agent)}
        </Grid>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Metadata (redacted)
        </Typography>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1,
            bgcolor: 'grey.100',
            borderRadius: 1,
            fontSize: '0.8rem',
            overflow: 'auto',
            maxHeight: 360
          }}
        >
          {entry.metadata == null ? '—' : JSON.stringify(entry.metadata, null, 2)}
        </Box>
      </Paper>
    </Box>
  );
}
